import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Layers, Boxes } from "lucide-react";
import { Link } from "react-router-dom";
import { inputCls, fmtNumber } from "./shared";
import Field from "./Field";

// Build floor labels from a property's floor count.
// Example: floors_count=5 → ["Ground", "First", "Second", "Third", "Fourth"]
// floors_count=0/missing → ["Ground"] (always at least the ground floor available)
const FLOOR_NAMES = [
  "Ground", "First", "Second", "Third", "Fourth", "Fifth", "Sixth", "Seventh",
  "Eighth", "Ninth", "Tenth", "Eleventh", "Twelfth", "Thirteenth", "Fourteenth",
  "Fifteenth", "Sixteenth", "Seventeenth", "Eighteenth", "Nineteenth", "Twentieth",
];
function floorsFor(prop) {
  const n = Math.max(1, Number(prop?.floors_count) || 1);
  return FLOOR_NAMES.slice(0, Math.min(n, FLOOR_NAMES.length));
}

const empty = {
  property_id: "",
  name: "",
  unit_type: "",
  floor: "Ground",
  area: 0,
  is_occupied: false,
  notes: "",
};

export default function UnitsTab({
  units,
  properties,
  lands,
  refresh,
  writable,
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [filterProp, setFilterProp] = useState("");
  const [unitTypes, setUnitTypes] = useState([]);
  const [itemCounts, setItemCounts] = useState({});

  useEffect(() => {
    api.get("/unit-item-counts").then(({ data }) => setItemCounts(data || {})).catch(() => setItemCounts({}));
  }, [units]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/masters/unit_type");
        setUnitTypes((data || []).map((d) => d.name));
      } catch {
        setUnitTypes([]);
      }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const propById = useMemo(
    () => Object.fromEntries(properties.map((p) => [p.id, p])),
    [properties],
  );
  const landById = useMemo(
    () => Object.fromEntries(lands.map((l) => [l.id, l])),
    [lands],
  );
  const filtered = filterProp
    ? units.filter((u) => u.property_id === filterProp)
    : units;

  // Floor options derive from the currently-selected parent property's floors_count
  const floorOptions = useMemo(
    () => floorsFor(propById[form.property_id]),
    [propById, form.property_id],
  );

  const openCreate = () => {
    setEditing(null);
    const propId = filterProp || (properties[0]?.id ?? "");
    const floors = floorsFor(propById[propId]);
    setForm({
      ...empty,
      property_id: propId,
      unit_type: unitTypes[0] || "",
      floor: floors[0] || "Ground",
    });
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ ...empty, ...u });
    setOpen(true);
  };

  // Keep Floor in-range whenever Parent Property changes (or initial form load).
  // Preserves the existing value if it is still a valid option for the chosen property.
  useEffect(() => {
    if (!open) return;
    if (!floorOptions.includes(form.floor)) {
      set("floor", floorOptions[0] || "Ground");
    }
  }, [form.property_id, open, floorOptions.join("|")]);

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Unit name is required");
      return;
    }
    if (!form.property_id) {
      toast.error("Pick a parent Property");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, area: Number(form.area) || 0 };
      if (editing) {
        await api.put(`/units/${editing.id}`, payload);
        toast.success("Unit updated");
      } else {
        await api.post(`/units`, payload);
        toast.success("Unit added");
      }
      setOpen(false);
      refresh();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Save failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const u = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/units/${u.id}`);
      toast.success("Unit removed");
      refresh();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed",
      );
    }
  };

  return (
    <div data-testid="units-tab">
      <div className="flex justify-between items-end mb-4 gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
            Filter Property
          </span>
          <select
            data-testid="unit-filter-property"
            className={inputCls + " w-72"}
            value={filterProp}
            onChange={(e) => setFilterProp(e.target.value)}
          >
            <option value="">All Properties</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {landById[p.land_id] ? ` · ${landById[p.land_id].name}` : ""}
              </option>
            ))}
          </select>
        </div>
        {writable && (
          <button
            onClick={openCreate}
            disabled={properties.length === 0}
            title={properties.length === 0 ? "Add a property first" : undefined}
            data-testid="unit-add-button"
            className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-40"
          >
            <Plus size={14} strokeWidth={1.5} /> Add Unit
          </button>
        )}
      </div>
      {filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-12 text-center">
          <Layers size={28} strokeWidth={1.5} className="mx-auto text-[#234333]" />
          <div className="mt-3 font-serif text-2xl text-[#1c1a17]">
            No units yet
          </div>
          <div className="mt-2 text-sm text-[#6b655b]">
            Units are floors, rooms, labs or shops within a property.
          </div>
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="units-table">
            <thead>
              <tr className="bg-[#f9f8f6] text-left">
                {["Name", "Property", "Type", "Floor", "Room No", "Area", "Status", "Movable / Immovable", ""].map(
                  (h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr
                  key={u.id}
                  data-testid={`unit-row-${u.id}`}
                  className="border-t border-[#e5e0d8] hover:bg-[#f9f8f6]/60"
                >
                  <td className="px-4 py-3 text-[#1c1a17]">{u.name}</td>
                  <td className="px-4 py-3 text-[#6b655b]">
                    {propById[u.property_id]?.name || "—"}
                  </td>
                  <td className="px-4 py-3 text-[#6b655b]">{u.unit_type || "—"}</td>
                  <td className="px-4 py-3 text-[#6b655b]">{u.floor || "—"}</td>
                  <td className="px-4 py-3 text-[#6b655b] font-mono text-xs">
                    {u.assigned_room_no || "—"}
                  </td>
                  <td className="px-4 py-3 text-[#6b655b]">
                    {fmtNumber(u.area)} sqft
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block px-2 py-0.5 text-[10px] tracking-wide uppercase border rounded-sm ${
                        u.is_occupied
                          ? "bg-[#234333]/10 text-[#234333] border-[#234333]/30"
                          : "bg-[#d4af37]/10 text-[#8a6d1d] border-[#d4af37]/30"
                      }`}
                    >
                      {u.is_occupied ? "Occupied" : "Vacant"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#1c1a17] whitespace-nowrap" data-testid={`unit-item-counts-${u.id}`}>
                    {itemCounts[u.id]?.movable || 0} / {itemCounts[u.id]?.immovable || 0}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Link
                      to={`/admin/properties/units/${u.id}/items`}
                      data-testid={`unit-items-${u.id}`}
                      className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm mr-2"
                    >
                      <Boxes size={14} strokeWidth={1.5} /> Items
                    </Link>
                    <button
                      onClick={() => openEdit(u)}
                      data-testid={`unit-edit-${u.id}`}
                      className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                    >
                      <Pencil size={14} strokeWidth={1.5} />{" "}
                      {writable ? "Edit" : "View"}
                    </button>
                    {writable && (
                      <button
                        onClick={() => setConfirmDelete(u)}
                        data-testid={`unit-delete-${u.id}`}
                        className="ml-2 inline-flex items-center gap-1 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm"
                      >
                        <Trash2 size={14} strokeWidth={1.5} /> Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#e5e0d8] rounded-sm max-w-2xl"
          data-testid="unit-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {editing ? "Edit Unit" : "Add Unit"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
            <Field label="Parent Property *" full>
              <select
                data-testid="unit-property-select"
                className={inputCls}
                value={form.property_id}
                disabled={!writable}
                onChange={(e) => set("property_id", e.target.value)}
              >
                <option value="">— Select Property —</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {landById[p.land_id] ? ` · ${landById[p.land_id].name}` : ""}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Unit Name *">
              <input
                data-testid="unit-name-input"
                className={inputCls}
                value={form.name}
                disabled={!writable}
                onChange={(e) => set("name", e.target.value)}
                placeholder="e.g., Lab 101"
              />
            </Field>
            <Field label="Unit Type">
              <select
                data-testid="unit-type-select"
                className={inputCls}
                value={form.unit_type}
                disabled={!writable}
                onChange={(e) => set("unit_type", e.target.value)}
              >
                <option value="">— Select —</option>
                {unitTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
                {form.unit_type && !unitTypes.includes(form.unit_type) ? (
                  <option value={form.unit_type}>{form.unit_type}</option>
                ) : null}
              </select>
            </Field>
            <Field label="Floor">
              <select
                data-testid="unit-floor-select"
                className={inputCls}
                value={form.floor}
                disabled={!writable || !form.property_id}
                onChange={(e) => set("floor", e.target.value)}
              >
                {floorOptions.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
                {form.floor && !floorOptions.includes(form.floor) ? (
                  <option value={form.floor}>{form.floor} (legacy)</option>
                ) : null}
              </select>
              {form.property_id ? (
                <div className="text-[11px] text-[#6b655b] mt-1" data-testid="unit-floor-hint">
                  Property has {floorOptions.length} floor{floorOptions.length === 1 ? "" : "s"} configured.
                </div>
              ) : (
                <div className="text-[11px] text-[#9e2a2b] mt-1">Select a property first.</div>
              )}
            </Field>
            <Field label="Area (sqft)">
              <input
                type="number"
                className={inputCls}
                value={form.area}
                disabled={!writable}
                onChange={(e) => set("area", e.target.value)}
              />
            </Field>
            <Field label="Assigned Room No">
              <input
                type="text"
                data-testid="unit-assigned-room-input"
                className={inputCls}
                value={form.assigned_room_no}
                disabled={!writable}
                maxLength={32}
                placeholder="e.g. A-101, G12, LAB-3B"
                onChange={(e) =>
                  set(
                    "assigned_room_no",
                    // Allow letters, digits, dash, slash, space (alpha-numeric with common separators)
                    e.target.value.replace(/[^A-Za-z0-9\-/ ]/g, "").toUpperCase(),
                  )
                }
              />
              <div className="text-[11px] text-[#6b655b] mt-1">
                Alpha-numeric only (A–Z, 0–9, dash/slash). Auto-uppercased.
              </div>
            </Field>
            <Field label="Status" full>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_occupied}
                  disabled={!writable}
                  onChange={(e) => set("is_occupied", e.target.checked)}
                  className="w-4 h-4 accent-[#234333]"
                />
                <span className="text-sm text-[#1c1a17]">Currently occupied</span>
              </label>
            </Field>
            <Field label="Notes" full>
              <textarea
                className={inputCls}
                rows={2}
                value={form.notes}
                disabled={!writable}
                onChange={(e) => set("notes", e.target.value)}
              />
            </Field>
          </div>
          <DialogFooter className="pt-4">
            <button
              onClick={() => setOpen(false)}
              className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
            >
              Cancel
            </button>
            {writable && (
              <button
                onClick={save}
                disabled={saving}
                data-testid="unit-save"
                className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
              >
                {saving ? "Saving…" : editing ? "Update" : "Create"}
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
      >
        <AlertDialogContent className="bg-white border-[#e5e0d8] rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Remove this unit?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              {confirmDelete?.name} will be removed. Blocked if it has active
              allocations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white text-[#1c1a17] border-[#e5e0d8] rounded-sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-[#9e2a2b] hover:bg-[#7d2122] text-white rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
